import { useAuthStore } from '../stores/authStore'
import { useLogger } from '../composables/useLogger'
import { storeToRefs } from 'pinia'
import { OnboardingMethod } from '~/src/types'
import { ONBOARDING_METHOD } from '~/src/constants'

async function checkNeedToRedirectToUser(): Promise<boolean> {
    const logger = useLogger()
    const authStore = useAuthStore()

    // check if we are locked to user device
    const isLockedToUserDevice = authStore.isLockedToUserDevice
    logger.debug("Index Router: before check: isLockedToUserDevice", { isLockedToUserDevice: isLockedToUserDevice })
    if (isLockedToUserDevice) {
        logger.debug("Index Router: before check: isLockedToUserDevice, redirecting to user device")
        return true
    }

    // checks for need to redirect to User due to latest area stored in authStore
    const latestAreaBefore = authStore.latestArea
    logger.debug("Index Router: before check: latestAreaBefore", { latestAreaBefore: latestAreaBefore })
    
    const shouldRedirectBecauseOfStoredArea = await authStore.shouldRedirectToUserDueToLatestArea()
    logger.debug("Index Router: after check: shouldRedirect", { shouldRedirect: shouldRedirectBecauseOfStoredArea })
    const latestAreaAfter = authStore.latestArea
    logger.debug("Index Router: after check: latestAreaAfter", { latestAreaAfter: latestAreaAfter })

    if (shouldRedirectBecauseOfStoredArea) {
        logger.debug("Index Router: checkNeedToRedirectToUser: redirecting to latest area (which is User)")
        logger.debug("Index Router: checkNeedToRedirectToUser: latest area", { latestArea: authStore.latestArea })
        return true
    } else {
        logger.debug("Index Router: checkNeedToRedirectToUser: no redirect needed")
    }
    return false
}

export default defineNuxtRouteMiddleware(async (to, from) => {
    const logger = useLogger()
    logger.debug("Index Router: beforeEach: to", { to: to })
    logger.debug("Index Router: beforeEach: from", { from: from })

    const pathsWithoutAuth = [
        '/login',
        '/signup',
        '/onboard-wiz',
        '/what-is-this',
        '/password-reset',
        '/password-reset-confirm',
        '/auth/callback-invite',
        '/landings/landing-invite',
        '/start',
        '/account-deleted',
        '/test/test-deep-link',
        '/user/clock',
    ]

    // re-check auth state
    const authStore = useAuthStore()
    await authStore.checkAuth()

    logger.debug("Index Router: beforeEach: authStore", { authStore: authStore })

    // If this is the root path and there's no auth data, redirect to clock, depending the onboarding method
    if (to.path === '/' ) {
        if (!authStore.userLoggedIn && !authStore.helperLoggedIn)
        {
            if (ONBOARDING_METHOD === OnboardingMethod.ClockFocused) {
                logger.debug("Index Router: beforeEach: no auth data, redirecting to clock")
                return navigateTo('/user/clock')
            } else if (ONBOARDING_METHOD === OnboardingMethod.ClockFocusedToUserHome) {
                logger.debug("Index Router: beforeEach: no auth data, redirecting to user home")
                return navigateTo('/user/clock')
            } else if (ONBOARDING_METHOD === OnboardingMethod.Wizard) {
                logger.debug("Index Router: beforeEach: no auth data, redirecting to wizard")
                return navigateTo('/user/clock')
            }
        }
        
        // If there is auth data, check if we need to redirect to User area for whatever reason, ignoring
        // is we have knowledge of Helper
        logger.debug("Index Router: beforeEach: path is root, checking need to redirect to User")
        if (await checkNeedToRedirectToUser()) {
            logger.debug("Index Router: beforeEach: need to redirect to User, redirecting")
            return navigateTo('/user/home')
        }
    }

    if (authStore.mustChooseHelperRole) {
        logger.debug("Index Router: beforeEach: must choose helper role, checking if we are on the select-user page")
        if (!to.path.startsWith('/select-user')) {
            logger.debug("Index Router: beforeEach: must choose helper role, redirecting to select-user")
            return navigateTo('/select-user')
        } else {
            logger.debug("Index Router: beforeEach: must choose helper role, but we are on the select-user page, so we can continue")
            return
        }
    }

    if (pathsWithoutAuth.includes(to.path)) {
        logger.debug("Index Router: beforeEach: path is in pathsWithoutAuth, just navigate, no auth checks needed")
    } else {
        if (to.path.startsWith('/user')) {
            logger.debug("Index Router: beforeEach: path is to user page, checking User user is logged in")
            if (!authStore.userLoggedIn) {
                logger.debug("Index Router: beforeEach: user is not logged in, redirecting to login")
                return navigateTo('/login')
            }
        } else if (to.path.startsWith('/admin')) {
            logger.debug("Index Router: beforeEach: path is to admin page, checking User user is logged in")
            if (!authStore.isAdmin) {
                logger.debug("Index Router: beforeEach: user is not admin, redirecting to login")
                return navigateTo('/login')
            }
        } else { // in helper area
            logger.debug("Index Router: beforeEach: path is not to user page or pathsWithoutAuth, must be straight helper, checking helper user is logged in and we are not locked to user device")
            if (authStore.isLockedToUserDevice) {
                logger.error("Index Router: beforeEach: we are locked to user device but trying to access helper area, this should not happen, redirecting to user home")
                return navigateTo('/user/home')
            }

            const helperLoggedIn = authStore.helperLoggedIn
            logger.debug("Index Router: beforeEach: helperLoggedIn", { helperLoggedIn: helperLoggedIn })
            if (!helperLoggedIn) {
                logger.debug("Index Router: beforeEach: helper is not logged in, redirecting to login")
                return navigateTo('/login')
            }
        }
        // sure, we can go there, record area we are going to before 
        logger.debug("Index Router: beforeEach: All good, can navigate to", { to: to })
        await authStore.setLatestArea(to.path.startsWith('/user') ? 'User' : 'Helper')
    }
    // auth is consistent with the page we are on, now do further adaptions fix-ups

    // make all user pages use the user layout, unless overridden by page meta
    if (to.path.startsWith('/user')) {
        const pageLayout = to.meta.layout
        if (pageLayout) {
            setPageLayout(pageLayout)
        } else {
            setPageLayout('userlayout')
        }
    }
})
